import { useLayoutEffect, useRef, useState, type Dispatch } from 'react';
import { chipsContextOf, type FilterChip } from '../results/filter_chips_state.js';
import type { FlightFilterIntent } from '../results/filter_intent_source.js';
import { defaultFilterState, priceBounds } from '../results/filter_logic.js';
import type { FlightFilterNote as FilterTextNote } from '../results/filters/filter_chips_copy.js';
import type { FlightExplorerTrack } from './explorer_env.js';
import type { ExplorerEvent, ExplorerState } from './explorer_state.js';
import type {
  FlightExplorerOffer,
  FlightExplorerRequest,
  FlightSearchFacets,
  FlightSearchFn,
} from './search_source.js';

export interface FilterIntentControl {
  chips: FilterChip[];
  note: FilterTextNote | undefined;
  /** Interpolação da nota corrente (`t('flightChips.note.<note>', noteValues)`) — só quando a
   *  nota usa valores (ex.: `{{cabin}}`, R2-D4). */
  noteValues?: Record<string, string>;
  busy: boolean;
  submit: (text: string) => void;
  remove: (id: string) => void;
  /** Deixa uma nota fora do ciclo de `submit` — hoje só `useCabinRequest` (R2-D4): o pedido de
   *  cabine por texto é consumido no reducer e a resposta (busca refeita, mesma cabine, sem
   *  seam) não nasce de uma interpretação nova. */
  notify: (note: FilterTextNote, values?: Record<string, string>) => void;
}

/** O que o `submit` precisa ler NO MOMENTO do disparo (e não no do render que o criou):
 *  âncora, a perna em cena, a geração de busca de CADA perna e as duas fontes do `ChipsContext`.
 *  Perna e geração viajam SEPARADAS de propósito — ver `staleness`. */
interface FilterIntentLive {
  anchor: string | undefined;
  activeLeg: number;
  seqs: number[];
  offers: FlightExplorerOffer[];
  facets: FlightSearchFacets | undefined;
}

/**
 * O que mudou entre o pedido e a resposta do juiz (#3559). Antes isso era uma chave só
 * (`${activeLeg}:${seqs[activeLeg]}`), e ela confundia duas coisas diferentes:
 *
 * - `stale`: a GERAÇÃO da perna em que se pediu andou — uma busca nova pousou ali e a lista virou
 *   outra. O corte cairia sobre uma lista que ninguém viu; a resposta morre.
 * - `leg_moved`: aquela lista é a mesma, a pessoa só passou para OUTRA perna enquanto o juiz
 *   pensava (escolheu a ida, o ✕ reabriu um trecho). Nada da lista se perdeu.
 * - `fresh`: nada mudou.
 */
function staleness(
  live: FilterIntentLive,
  leg: number,
  seq: number,
): 'fresh' | 'leg_moved' | 'stale' {
  if ((live.seqs[leg] ?? 0) !== seq) return 'stale';
  return live.activeLeg === leg ? 'fresh' : 'leg_moved';
}

/**
 * A resposta ainda serve depois de a perna em cena ter trocado? Só quando ela DIZ em que perna
 * cada corte cai: `resolveChips` roteia por `leg` (o da perna ativa entra no filtro, os das
 * outras ficam `deferred`), então um chip `applied` SEM `leg` pousaria na perna errada — a que a
 * pessoa acabou de abrir, não a que ela olhava quando digitou. `control` (reset) não tem perna
 * nenhuma pra rotear e zeraria o filtro de uma perna que não foi a pedida.
 */
function acceptsAfterLegChange(intent: FlightFilterIntent): boolean {
  return (
    intent.kind === 'filter' &&
    intent.chips.every(
      // Cabine fica de fora mesmo com `leg`: ela desvia do `resolveChips` (R2-D4 — vira
      // `cabinRequest`) e o `useCabinRequest` chama `cabin.onSearch` da perna ATIVA, então o
      // `leg` do chip não roteia nada e a busca sairia na perna nova com a cabine da antiga.
      (chip) => chip.status !== 'applied' || (chip.leg !== undefined && !chip.patch?.cabin),
    )
  );
}

/** Um id de chip por submissão — só precisa ser único DENTRO desta tela (o reducer
 *  casa o pendente com a resposta por ele). */
function chipId(): string {
  // Web Crypto, não `Math.random` (Sonar S2245): mesmo papel, sufixo base36 curto.
  const [random = 0] = crypto.getRandomValues(new Uint32Array(1));
  return `chip_${Date.now().toString(36)}_${random.toString(36).slice(0, 6)}`;
}

/**
 * A NOTA que o intent deixa (extraída do `submit` pra manter a complexidade dele sob o
 * teto): `kind` ≠ filter fala por si; `filter` sem chip nenhum é ruído — o juiz leu
 * como filtro e não achou eixo nenhum, e sem nota a tela só engolia o texto.
 */
function noteOf(intent: {
  kind: FilterTextNote | 'filter';
  chips: unknown[];
}): FilterTextNote | undefined {
  if (intent.kind !== 'filter') return intent.kind;
  return intent.chips.length === 0 ? ('noise' as const) : undefined;
}

/**
 * A telemetria do filtro por texto. O TEXTO DA PESSOA NUNCA ENTRA: só o que o juiz
 * respondeu (que tipo, quantos chips, quantos aplicaram, qual juiz) e a latência —
 * o registry do domain (`flight_filter_text`) é o contrato.
 *
 * `legs` é o TAMANHO do roteiro (`legLabelsOf(request)?.length`, `undefined` no one-way — mesma
 * fonte que decide a pergunta `perna` do juiz). Sem o limite, um chip com `leg` fora do roteiro
 * (que `resolveLeggedChip` vira `unresolved`, nunca aplica de verdade) ou QUALQUER `leg !== 0` num
 * one-way (onde perna nem é eixo) inflava `deferred` (Finding 2 da revisão final).
 */
export function trackIntent(
  track: FlightExplorerTrack,
  intent: {
    kind: string;
    chips: { status: string; leg?: number }[];
    judge?: string;
    control?: string;
  },
  startedAt: number,
  activeLeg: number,
  legs: number | undefined,
): void {
  track('flight_filter_text', {
    kind: intent.kind,
    chips: intent.chips.length,
    applied: intent.chips.filter((chip) => chip.status === 'applied').length,
    deferred: intent.chips.filter(
      (chip) =>
        legs !== undefined &&
        chip.leg !== undefined &&
        chip.leg < legs &&
        chip.leg !== activeLeg &&
        chip.status === 'applied',
    ).length,
    judge: intent.judge ?? 'unknown',
    ...(intent.control === undefined ? {} : { control: intent.control }),
    latencyMs: Date.now() - startedAt,
  });
}

/** Rótulos das pernas para a pergunta de perna do juiz: RT ⇒ ida/volta; multi ⇒ trecho_N; one-way ⇒ nenhum. */
function legLabelsOf(request: FlightExplorerRequest): string[] | undefined {
  if (request.mode === 'round_trip') return ['ida', 'volta'];
  if (request.mode === 'multi_leg') return request.legs.map((_, i) => `trecho_${String(i + 1)}`);
  return undefined;
}

/**
 * O IO do filtro por texto (spec flight-text-filter-chips §6): texto → chip pendente →
 * `interpretFilter` (seam) → `chip_resolved` (o reducer aplica os patches) → o `useServerFilter`
 * vê o filtro mudar e refina como sempre. Resposta que chega depois de uma BUSCA nova NA PERNA
 * EM QUE SE PEDIU (a geração `seqs[legAtSubmit]` andou) morre — seria um corte de outra lista.
 *
 * Trocar de PERNA no meio, porém, não invalida nada por si (#3559): "na volta, só direto" pedido
 * na ida + a escolha da ida antes de o juiz responder é o caminho normal do pedido, não um
 * acidente. A resposta é aceita quando diz em que perna cada corte cai (`acceptsAfterLegChange`)
 * — o `resolveChips` roteia — e só morre (com nota, o gesto foi da pessoa) quando cairia na perna
 * errada. O pendente sobrevive à perna que abre pelo `carryDeferred`; sem as duas metades o
 * pedido explícito por perna morria em silêncio.
 *
 * Sem seam ou congelado ⇒ `undefined` (o composer não é renderizado).
 */
export function useFilterIntent(input: {
  fs: FlightSearchFn | undefined;
  anchor: string | undefined;
  state: ExplorerState;
  send: Dispatch<ExplorerEvent>;
  offers: FlightExplorerOffer[];
  facets: FlightSearchFacets | undefined;
  frozen: boolean;
  locale: string;
  track: FlightExplorerTrack;
}): FilterIntentControl | undefined {
  const track = input.track;
  const [note, setNote] = useState<FilterTextNote | undefined>();
  const [noteValues, setNoteValues] = useState<Record<string, string> | undefined>();
  const snapshot = {
    anchor: input.anchor,
    activeLeg: input.state.activeLeg,
    seqs: input.state.seqs,
    offers: input.offers,
    facets: input.facets,
  };
  const live = useRef<FilterIntentLive>(snapshot);
  // The committed snapshot, for the answers that land later (staleness check).
  useLayoutEffect(() => {
    live.current = snapshot;
  });
  const interpret = input.fs?.interpretFilter;
  if (!interpret || input.frozen) return undefined;
  const { send, locale } = input;
  const submit = (text: string) => {
    const trimmed = text.trim();
    const anchor = live.current.anchor;
    if (!trimmed || !anchor) return;
    const id = chipId();
    const startedAt = Date.now();
    // A perna DO PEDIDO — é dela que a telemetria fala e é a geração DELA que invalida.
    const legAtSubmit = live.current.activeLeg;
    const seqAtSubmit = live.current.seqs[legAtSubmit] ?? 0;
    setNote(undefined);
    setNoteValues(undefined);
    send({ t: 'chip_pending', id, text: trimmed });
    const legs = legLabelsOf(input.state.request);
    interpret(anchor, { text: trimmed, locale, ...(legs ? { legs } : {}) }).then(
      (intent) => {
        const moved = staleness(live.current, legAtSubmit, seqAtSubmit);
        if (moved === 'stale' || (moved === 'leg_moved' && !acceptsAfterLegChange(intent))) {
          send({ t: 'chip_failed', id });
          // Perna trocada: o pedido morreu pelo gesto DA PESSOA, e sem nota o composer só
          // engoliria o texto. Busca nova (`stale`) segue muda — a tela inteira já é outra.
          if (moved === 'leg_moved') setNote('failed');
          return;
        }
        if (intent.kind === 'control') {
          // O pendente sai (chip_resolved com kind ≠ filter só remove) e, se é reset, o MESMO
          // evento do botão "Redefinir todos" — eixos, chips e deferred zeram juntos (R2-D6).
          send({
            t: 'chip_resolved',
            id,
            intent,
            ctx: chipsContextOf(live.current.offers, live.current.facets),
          });
          if (intent.control === 'reset') {
            const offers = live.current.offers;
            const ceiling = live.current.facets?.priceRange?.max ?? priceBounds(offers)[1];
            send({ t: 'filter_reset', filter: defaultFilterState(offers, ceiling) });
            setNote('reset');
          } else {
            setNote('noise');
          }
          trackIntent(track, intent, startedAt, legAtSubmit, legs?.length);
          return;
        }
        send({
          t: 'chip_resolved',
          id,
          intent,
          ctx: chipsContextOf(live.current.offers, live.current.facets),
        });
        setNote(noteOf({ kind: intent.kind, chips: intent.chips }));
        trackIntent(track, intent, startedAt, legAtSubmit, legs?.length);
      },
      () => {
        send({ t: 'chip_failed', id });
        setNote('failed');
        track('flight_filter_text', { kind: 'error', latencyMs: Date.now() - startedAt });
      },
    );
  };
  return {
    chips: input.state.chips,
    note,
    noteValues,
    busy: input.state.chips.some((chip) => chip.status === 'pending'),
    submit,
    remove: (id) => {
      send({ t: 'chip_removed', id });
    },
    notify: (next, values) => {
      setNote(next);
      setNoteValues(values);
    },
  };
}
